import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { AlertTriangle, Building2, Plus, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT, Bi, LANGUAGES, Lang } from "@/lib/i18n";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, PageTitle } from "@/components/common";
import { Field, BigInput, Chips } from "@/components/form";
import { Textarea } from "@/components/ui/textarea";
import { PhotosManager } from "@/components/Photos";
import { BottomSheet } from "@/components/BottomSheet";
import { PropertyForm } from "@/pages/Onboarding";
import { BackLink } from "@/pages/More";

function PropertySection() {
  const { t } = useT();
  const nav = useNavigate();
  const { property, properties, reloadProperties, selectProperty } = useAuth();
  const toggleId = async (v: boolean) => {
    try { await api.patch(`/properties/${property.id}`, { id_upload_enabled: v }); await reloadProperties(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    if (!window.confirm(`${t("delete_property")}: ${property.name}?`)) return;
    try {
      await api.delete(`/properties/${property.id}`);
      const list = await reloadProperties();
      if (list[0]) selectProperty(list[0].id); else nav("/onboarding");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card className="p-4 space-y-4" data-testid="settings-property-card">
      <p className="font-bold flex items-center gap-2"><Building2 className="h-4 w-4 text-emerald-700" /><Bi k="property_settings" /></p>
      <PropertyForm key={property.id} initial={property} onSaved={() => { reloadProperties(); toast.success(t("saved")); }} />
      <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3">
        <label className="flex items-center justify-between gap-3">
          <span className="text-sm font-semibold"><Bi k="id_upload_toggle" /></span>
          <Switch data-testid="settings-id-upload-switch" checked={property.id_upload_enabled} onCheckedChange={toggleId} />
        </label>
        <p className="text-xs text-amber-800 mt-2 flex gap-1"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />{t("id_upload_warning")}</p>
      </div>
      {properties.length > 0 && <Button data-testid="settings-delete-property-btn" variant="outline" onClick={del} className="w-full h-12 rounded-xl text-rose-600 border-rose-200"><Trash2 className="h-4 w-4 mr-1" />{t("delete_property")}</Button>}
    </Card>
  );
}

const AMENITIES: [string, string][] = [["wifi", "WiFi"], ["food", "Food"], ["ac", "AC"], ["laundry", "Laundry"], ["housekeeping", "Housekeeping"], ["parking", "Parking"], ["power_backup", "Power backup"], ["hot_water", "Hot water"], ["cctv", "CCTV"], ["gym", "Gym"]];

function ListingSection() {
  const { t } = useT();
  const { property, reloadProperties } = useAuth();
  const init = (property as any).listing || {};
  const [f, setF] = useState<any>({ published: false, city: "", locality: "", gender: "coed", amenities: [], description: "", ...init, contact_phone: init.contact_phone || "" });
  const toggleAm = (a: string) => setF({ ...f, amenities: f.amenities.includes(a) ? f.amenities.filter((x) => x !== a) : [...f.amenities, a] });
  const save = async () => {
    try {
      await api.patch(`/properties/${property.id}`, { listing: { ...f, contact_phone: f.contact_phone || null } });
      await reloadProperties();
      toast.success(t("saved"));
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card className="p-4 space-y-4" data-testid="settings-listing-card">
      <div>
        <p className="font-bold">{t("listing")}</p>
        <p className="text-xs text-slate-500 mt-0.5">{t("listing_help")}</p>
      </div>
      <label className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
        <span className="text-sm font-semibold">{t("publish_listing")}</span>
        <Switch data-testid="listing-publish-switch" checked={f.published} onCheckedChange={(v) => setF({ ...f, published: v })} />
      </label>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t("city")}><BigInput data-testid="listing-city-input" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} placeholder="Bengaluru" /></Field>
        <Field label={t("locality")}><BigInput data-testid="listing-locality-input" value={f.locality} onChange={(e) => setF({ ...f, locality: e.target.value })} /></Field>
      </div>
      <Field label={t("pg_for")}>
        <Chips testId="listing-gender" value={f.gender} onChange={(v) => setF({ ...f, gender: v })} options={[{ value: "boys", label: "Boys" }, { value: "girls", label: "Girls" }, { value: "coed", label: "Co-living" }]} />
      </Field>
      <Field label={t("amenities")}>
        <div className="flex flex-wrap gap-2">
          {AMENITIES.map(([a, label]) => (
            <button key={a} type="button" data-testid={`listing-amenity-${a}`} onClick={() => toggleAm(a)}
              className={`h-10 px-3.5 rounded-full border text-sm font-semibold pg-tap ${f.amenities.includes(a) ? "bg-emerald-700 border-emerald-700 text-white" : "bg-white border-slate-200 text-slate-700"}`}>{label}</button>
          ))}
        </div>
      </Field>
      <Field label={t("description")}><Textarea data-testid="listing-desc-input" rows={3} maxLength={1000} className="rounded-xl text-base bg-white" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
      <Field label={t("contact_phone")}><BigInput data-testid="listing-phone-input" inputMode="numeric" maxLength={10} value={f.contact_phone} onChange={(e) => setF({ ...f, contact_phone: e.target.value.replace(/\D/g, "") })} /></Field>
      <PhotosManager propertyId={property.id} />
      <Button data-testid="listing-save-btn" onClick={save} className="w-full h-12 rounded-xl bg-emerald-700 hover:bg-emerald-800">{t("save")}</Button>
    </Card>
  );
}

function ProfileSection() {
  const { t } = useT();
  const { owner, setOwner } = useAuth();
  const [f, setF] = useState({ name: owner.name || "", phone: owner.phone || "" });
  const save = async () => {
    try { const r = await api.patch("/auth/me", { name: f.name, ...(f.phone ? { phone: f.phone } : {}) }); setOwner(r.data); toast.success(t("saved")); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card className="p-4 space-y-3" data-testid="settings-profile-card">
      <p className="font-bold"><Bi k="profile" /></p>
      <Field label={t("your_name")}><BigInput data-testid="settings-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
      <Field label={t("email")}><BigInput data-testid="settings-email-input" type="email" value={owner.email || "-"} disabled /></Field>
      <Field label={`${t("phone_label")} (${t("optional")})`} hint={owner.phone_verified && f.phone === owner.phone ? <span data-testid="settings-phone-verified" className="text-emerald-700 font-semibold">{t("phone_verified")}</span> : undefined}><BigInput data-testid="settings-phone-input" inputMode="numeric" maxLength={10} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value.replace(/\D/g, "") })} /></Field>
      <Button data-testid="settings-profile-save-btn" onClick={save} className="w-full h-12 rounded-xl bg-emerald-700 hover:bg-emerald-800">{t("save")}</Button>
    </Card>
  );
}

function LanguageSection() {
  const { t, lang, setLang } = useT();
  const { owner, setOwner } = useAuth();
  const change = async (v: Lang) => {
    setLang(v);
    try { const r = await api.patch("/auth/me", { lang: v }); setOwner({ ...owner, ...r.data }); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card className="p-4 space-y-3" data-testid="settings-language-card">
      <p className="font-bold">{t("language")}</p>
      <p className="text-xs text-slate-500">{t("language_help")}</p>
      <Select value={lang} onValueChange={change}>
        <SelectTrigger data-testid="settings-language-select" className="h-12 rounded-xl bg-white"><SelectValue /></SelectTrigger>
        <SelectContent>
          {LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value} data-testid={`settings-language-${l.value}`}>{l.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </Card>
  );
}

function PrivacySection() {
  const { t } = useT();
  const { logout } = useAuth();
  const [txt, setTxt] = useState("");
  const del = async () => {
    try { await api.delete("/account", { data: { confirm: "DELETE" } }); logout(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card className="p-4 space-y-3 border-rose-200" data-testid="settings-privacy-card">
      <p className="font-bold"><Bi k="privacy" /></p>
      <p className="text-xs text-slate-500">{t("delete_account_help")}</p>
      <BigInput data-testid="settings-delete-confirm-input" value={txt} onChange={(e) => setTxt(e.target.value)} placeholder="DELETE" />
      <Button data-testid="settings-delete-account-btn" disabled={txt !== "DELETE"} onClick={del} className="w-full h-12 rounded-xl bg-rose-600 hover:bg-rose-700">{t("delete_account")}</Button>
    </Card>
  );
}

export default function Settings() {
  const { t } = useT();
  const [addOpen, setAddOpen] = useState(false);
  const { reloadProperties, selectProperty, property } = useAuth();
  return (
    <div>
      <BackLink />
      <PageTitle title={t("settings")} />
      <div className="space-y-4">
        <PropertySection />
        {property && <ListingSection key={property.id} />}
        <Button data-testid="settings-add-property-btn" variant="outline" onClick={() => setAddOpen(true)} className="w-full h-14 rounded-2xl border-dashed"><Plus className="h-4 w-4 mr-1" />{t("add_property")}</Button>
        <ProfileSection />
        <LanguageSection />
        <PrivacySection />
      </div>
      <BottomSheet open={addOpen} onOpenChange={setAddOpen} title={<Bi k="add_property" />} testId="add-property-sheet">
        <PropertyForm onSaved={async (p) => { await reloadProperties(); selectProperty(p.id); setAddOpen(false); toast.success(t("saved")); }} />
      </BottomSheet>
    </div>
  );
}
